<template>
  <div class="subscriptions-index">
        <div class="row">
        <div class="col-md-4 col-sm-12">
          <div class="text-left py-3">
            <h3 class="text-weight-900 text-primary">Subscriptions</h3>
          </div>
        </div>

        <div class="col-md-8 col-sm-12">
          <div class="py-md-3 pb-3">
            <!-- Phones show Front desk / New / More only; Packages + Group Daily move into More. -->
            <div class="d-flex flex-wrap align-items-center justify-content-start justify-content-md-end">
                <button
                  v-if="can('subscriptions.checkin')"
                  class="btn btn-sm btn-primary mx-1 mb-1"
                  @click="$refs.attendanceModal.show()"
                >
                  <i class="bi bi-door-open mr-1"></i>Front desk
                </button>
                <button
                  v-if="can('subscriptions.create')"
                  class="btn btn-sm btn-outline-primary mx-1 mb-1"
                  @click="showCreateModal"
                >
                  <i class="fas fa-plus"></i> New<span class="d-none d-sm-inline"> Subscription</span>
                </button>
                <a
                  v-if="can('subscriptions.manage_packages')"
                  title="Packages"
                  href="/subscription-packages"
                  class="btn btn-sm btn-outline-primary mx-1 mb-1 d-none d-md-inline-block"
                >Subscription Packages</a>
                <button
                  class="btn btn-sm btn-outline-secondary mx-1 mb-1 d-none d-md-inline-block"
                  :title="groupDaily ? 'Show all subscription records' : 'Fold consecutive daily subscriptions into one row per customer'"
                  @click="groupDaily = !groupDaily"
                >
                  <i :class="groupDaily ? 'bi bi-layers-fill' : 'bi bi-layers'"></i>
                  {{ groupDaily ? 'Ungrouped' : 'Group Daily' }}
                </button>
              <div class="dropdown mx-1 mb-1">
                <button
                  class="btn-sm btn btn-outline-secondary dropdown-toggle"
                  data-toggle="dropdown"
                  aria-expanded="false"
                  title="More actions"
                >
                  More
                </button>
                <div class="dropdown-menu dropdown-menu-right sub-dropdown">
                  <a
                    v-if="can('subscriptions.manage_packages')"
                    class="dropdown-item d-md-none"
                    href="/subscription-packages"
                  >
                    <i class="bi bi-box-seam"></i> Subscription Packages
                  </a>
                  <a class="dropdown-item d-md-none" href="#" @click.prevent="groupDaily = !groupDaily">
                    <i :class="groupDaily ? 'bi bi-layers-fill' : 'bi bi-layers'"></i>
                    {{ groupDaily ? 'Show all records' : 'Group daily passes' }}
                  </a>
                  <a
                    v-if="can('subscriptions.manage_wallet')"
                    class="dropdown-item"
                    href="#"
                    @click.prevent="$refs.walletModal.show()"
                  >
                    <i class="bi bi-wallet"></i> Load Customer Wallet
                  </a>
                  <a
                    v-if="can('subscriptions.view_financials')"
                    class="dropdown-item"
                    href="#"
                    @click.prevent="$refs.statementsModal.show()"
                  >
                    <i class="bi bi-file"></i> Subscription Statements
                  </a>
                  <a
                    v-if="can('subscriptions.manage_qrcodes')"
                    class="dropdown-item"
                    href="#"
                    @click.prevent="$refs.qrcodeManagerModal.show()"
                  >
                    <i class="bi bi-qr-code-scan"></i> QRCode Manager
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    <ul v-if="can('subscriptions.view_dashboard')" class="nav nav-pills mb-3">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: tab === 'list' }" href="#" @click.prevent="setTab('list')">
          <i class="bi bi-list-ul mr-1"></i>Subscriptions
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: tab === 'overview' }" href="#" @click.prevent="setTab('overview')">
          <i class="bi bi-graph-up mr-1"></i>Overview
        </a>
      </li>
    </ul>

    <subscription-overview v-if="tab === 'overview'" />

    <div v-show="tab === 'list'">
    <b-row class="mb-4">
      <b-col md="4" class="mb-2 mb-md-0">
        <b-card class="text-center">
          <h3 class="text-secondary">{{ summary.active }}</h3>
          <p class="mb-0">Active</p>
        </b-card>
      </b-col>
      <b-col md="4" class="mb-2 mb-md-0">
        <b-card class="text-center">
          <h3 class="text-muted">{{ summary.expired }}</h3>
          <p class="mb-0">Expired</p>
        </b-card>
      </b-col>

      <b-col v-if="can('subscriptions.view_financials')" md="4" class="mb-2 mb-md-0">
        <b-card class="text-center">
          <h3 class="text-primary">KES {{ displayRevenue(summary.total_revenue || 0) }}</h3>
          <p class="mb-0">
            Revenue (Month)
            <button
              class="btn btn-link btn-sm p-0 ml-1 text-muted"
              :title="showRevenue ? 'Hide Revenue' : 'Show Revenue'"
              @click="showRevenue = !showRevenue"
            >
              <i :class="showRevenue ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
            </button>
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filters -->
    <b-card class="mb-3">
      <b-row>
        <b-col sm="6" md="3">
          <b-form-group label="Status">
            <b-form-select v-model="filters.status" :options="statusOptions" @change="loadSubscriptions" />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3">
          <b-form-group label="Package">
            <b-form-select v-model="filters.package_id" :options="packageOptions" @change="loadSubscriptions" />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3">
          <b-form-group label="Customer">
            <div class="position-relative">
              <b-form-input
                v-model="customerSearch"
                placeholder="Search by name or phone…"
                @input="onCustomerSearch"
                autocomplete="off"
              />
              <div
                v-if="customerSuggestions.length"
                class="sub-customer-dropdown"
              >
                <a
                  v-for="c in customerSuggestions"
                  :key="c.id"
                  class="sub-customer-dropdown__item"
                  href="#"
                  @click.prevent="selectCustomer(c)"
                >
                  {{ c.full_name }}
                  <small class="text-muted ml-1">{{ c.phone }}</small>
                </a>
              </div>
              <a
                v-if="filters.customer_id"
                href="#"
                class="sub-clear-customer"
                @click.prevent="clearCustomer"
              ><i class="bi bi-x"></i></a>
            </div>
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3">
             <b-form-group label="Dates">
                  <date-range-picker :auto-apply="autoApply" v-model="filters" :ranges="ranges" :opens="datepicker_opens" :append-to-body="append_to_body" class="custom-width-datepicker">
                                <template #input="picker" style="width: 100% !important;">
                                                                    <p v-if="picker.startDate !==null && picker.endDate !==null">{{ formatDate(picker.startDate,'DD-MMM-YYYY') }} - {{ formatDate(picker.endDate,'DD-MMM-YYYY') }}</p>
                                                                    <p v-else><i class="bi bi-calendar-event"></i>  Filter by Date</p>
</template>
                                            </date-range-picker>
                                             </b-form-group>
        </b-col>
      </b-row>
      <b-button variant="outline-secondary" size="sm" @click="resetFilters">Clear Filters</b-button>
    </b-card>



    <!-- Table -->
    <b-card>
      <b-table
        :items="displayedSubscriptions"
        :fields="fields"
        :busy="loading"
        responsive
        stacked="md"
        striped
        hover

         @row-clicked="viewSubscription"
      >
        <template #cell(customer)="data">
          {{ data.item.customer.full_name }}
          <span v-if="can('subscriptions.view_financials') && data.item.customer && data.item.customer.wallet && data.item.customer.wallet.balance > 0" class="text-muted ml-1">
            - <i class="fas fa-wallet"></i> (KES {{ fmtAmount(data.item.customer.wallet.balance) }})
          </span>
        </template>

        <template #cell(package)="data">
          {{ data.item.package.name }} - Kshs.{{ convertCurrencyToLocal(data.item.package.price) }}
          <span v-if="data.item._grouped_count > 1" class="badge badge-secondary ml-1"
                :title="`${data.item._grouped_count} daily periods (showing latest)`">
            {{ data.item._grouped_count }} periods
          </span>
        </template>

        <template #cell(period)="data">
          {{ formatDate(data.item.start_date, 'DD-MM-YYYY') }} to {{ formatDate(data.item.end_date, 'DD-MM-YYYY') }}
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item.status)">
            {{ data.item.status }}
          </b-badge>
        </template>

        <template #cell(auto_renew)="data">
          <i :class="data.item.is_auto_renew ? 'fas fa-check text-success' : 'fas fa-times text-muted'"></i>
        </template>

        <!-- Balance Due — only rendered when field is present (view_financials check is in computed fields) -->
        <template #cell(balance_due)="data">
          <span
            v-if="data.item.balance_due > 0"
            class="badge badge-danger"
          >
            KES {{ fmtAmount(data.item.balance_due) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="data">
          <div class="dropdown">
            <a type="button" data-toggle="dropdown" aria-expanded="false">
              <i class="bi bi-three-dots-vertical"></i>
            </a>
            <div class="dropdown-menu">
              <a class="dropdown-item" @click="viewSubscription(data.item)">View</a>
              <a
                v-if="can('subscriptions.checkin') && data.item.status === 'active'"
                class="dropdown-item text-success"
                @click="promptCheckIn(data.item)"
              ><i class="bi bi-box-arrow-in-right mr-1"></i>Check In</a>
              <a
                v-if="can('subscriptions.renew')"
                class="dropdown-item"
                @click="renewSubscription(data.item)"
              >Renew</a>
              <a
                v-if="can('subscriptions.cancel')"
                class="dropdown-item"
                @click="cancelSubscription(data.item)"
              >Cancel</a>
            </div>
          </div>
        </template>
      </b-table>

      <b-pagination
        v-model="currentPage"
        :total-rows="subscriptions.total"
        :per-page="subscriptions.per_page"
        @change="loadSubscriptions"
        align="center"
      ></b-pagination>
    </b-card>

    </div><!-- /list tab -->

    <!-- Modals -->
    <create-subscription-modal ref="createModal" @created="loadSubscriptions" />
    <renew-subscription-modal ref="renewModal" @renewed="loadSubscriptions" />
    <cancel-subscription-modal ref="cancelModal" @cancelled="loadSubscriptions" />
    <subscription-details-modal ref="detailsModal" />
   <load-customer-wallet-modal ref="walletModal" />

    <attendance-dashboard-modal ref="attendanceModal" />
    <subscription-statements-modal  ref="statementsModal" />
    <subscription-qr-code-manager-modal ref="qrcodeManagerModal" />

    <!-- Quick Check-In Confirmation Modal -->
    <b-modal
      ref="checkInModal"
      title="Confirm Check-In"
      ok-title="Check In"
      ok-variant="success"
      :ok-disabled="checkInLoading"
      @ok.prevent="confirmCheckIn"
      @hidden="checkInTarget = null"
    >
      <div v-if="checkInTarget">
        <p>Check in <strong>{{ checkInTarget.customer.full_name }}</strong>?</p>
        <p class="text-muted mb-0">Package: {{ checkInTarget.package.name }}</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
        import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";
        import LoadCustomerWalletModal from "./modals/LoadCustomerWalletModal.vue";
        import AttendanceDashboardModal from "./modals/AttendanceDashboardModal.vue";
        import SubscriptionStatementsModal from "./modals/SubscriptionStatementsModal.vue";
        import SubscriptionQrCodeManagerModal from "./modals/SubscriptionQrCodeManagerModal.vue";
import subscriptionsAdminMixin from '../../mixins/subscriptionsAdminMixin.js';
import SubscriptionOverview from './SubscriptionOverview.vue';



export default {
  name: 'SubscriptionsIndex',
  mixins: [subscriptionsAdminMixin],

  props: {
    // Passed by SubscriptionDashboard when a status card is clicked
    initialStatus: { type: String, default: null },
  },

  components: {
    DateRangePicker,
    AttendanceDashboardModal,
    LoadCustomerWalletModal,
    SubscriptionStatementsModal,
    SubscriptionQrCodeManagerModal,
    SubscriptionOverview,
  },

  data() {
    return {
      tab: new URLSearchParams(window.location.search).get('tab') === 'overview' ? 'overview' : 'list',
      subscriptions: {
        data: [],
        total: 0,
        per_page: 20
      },
      summary: {
        active: 0,
        expired: 0,
        past_due: 0,
        total_revenue: 0
      },
      filters: {
        status: null,
        package_id: null,
        customer_id: null,
        startDate: null,
        endDate: null
      },
      dateRange: {
        startDate: null,
        endDate: null,
      },
      customerSearch: '',
      customerSuggestions: [],
      customerSearchTimer: null,

      autoApply: true,
      append_to_body: true,
      datepicker_opens: 'right',
      ranges: false,
      currentPage: 1,
      loading: false,

      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: 'active', text: 'Active' },
        { value: 'expired', text: 'Expired' },
        { value: 'cancelled', text: 'Cancelled' },
        { value: 'past_due', text: 'Past Due' }
      ],
      packageOptions: [],
      checkInTarget: null,
      checkInLoading: false,
      groupDaily: false,
      showRevenue: true,
    }
  },

  computed: {
    displayedSubscriptions() {
      if (!this.groupDaily) return this.subscriptions.data

      const groups = new Map()
      this.subscriptions.data.forEach(sub => {
        const isDailyPackage = sub.package && sub.package.duration_unit === 'day'
        if (!isDailyPackage) {
          // Non-daily: always show as individual rows
          groups.set(sub.id, { ...sub, _grouped_count: 1 })
          return
        }
        const key = `${sub.customer_id}:${sub.subscription_package_id}`
        if (!groups.has(key)) {
          groups.set(key, { ...sub, _grouped_count: 1 })
        } else {
          const existing = groups.get(key)
          const count    = existing._grouped_count + 1
          // Keep the entry with the latest end_date
          if (new Date(sub.end_date) > new Date(existing.end_date)) {
            groups.set(key, { ...sub, _grouped_count: count })
          } else {
            existing._grouped_count = count
          }
        }
      })
      return Array.from(groups.values())
    },

    fields() {
      const f = [
        { key: 'customer',   label: 'Customer',   sortable: true },
        { key: 'package',    label: 'Package',    sortable: true },
        { key: 'period',     label: 'Period' },
        { key: 'status',     label: 'Status',     sortable: true },
        { key: 'auto_renew', label: 'Auto-Renew' },
      ]
      // Only add balance column if user can see financials
      if (this.can('subscriptions.view_financials')) {
        f.push({ key: 'balance_due', label: 'Balance Due', sortable: false })
      }
      f.push({ key: 'actions', label: 'Actions' })
      return f
    },
  },

  watch: {
    initialStatus(val) {
      if (val) {
        this.filters.status = val
        this.loadSubscriptions()
      }
    },
  },

  mounted() {
    if (this.initialStatus) this.filters.status = this.initialStatus
    this.loadSubscriptions()
    this.loadPackages()
    if (new URLSearchParams(window.location.search).has('openCreate')) {
      this.showCreateModal()
    }
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('subscription-id')) {
      this.viewSubscription({ id: urlParams.get('subscription-id') })
    }
  },

  methods: {
    setTab(tab) {
      this.tab = tab
      try {
        const url = new URL(window.location.href)
        tab === 'list' ? url.searchParams.delete('tab') : url.searchParams.set('tab', tab)
        window.history.replaceState(null, '', url.toString())
      } catch (e) { /* ignore */ }
    },

    async loadSubscriptions() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          status:      this.filters.status,
          package_id:  this.filters.package_id,
          customer_id: this.filters.customer_id || undefined,
          date_from:   this.filters.startDate ? moment(this.filters.startDate).format('YYYY-MM-DD') : undefined,
          date_to:     this.filters.endDate   ? moment(this.filters.endDate).format('YYYY-MM-DD')   : undefined,
        };

        const response = await axios.get('/api/subscriptions', { params });
        this.subscriptions = response.data.subscriptions;
        this.summary = response.data.summary;
      } catch (error) {
        this.$bvToast.toast('Failed to load subscriptions', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },
    formatDate(date, format) {
                let finaldate = moment(date).format(format);
                return finaldate;
            },

    async loadPackages() {
      try {
        const response = await axios.get('/api/subscription-packages');
        this.packageOptions = [
          { value: null, text: 'All Packages' },
          ...response.data.map(pkg => ({ value: pkg.id, text: pkg.name }))
        ];
      } catch (error) {
        console.error('Failed to load packages', error);
      }
    },

    showCreateModal() {
      this.$refs.createModal.show();
    },

    viewSubscription(subscription) {
        // console.log(subscription)
       this.$refs.detailsModal.show(subscription);
    },

    renewSubscription(subscription) {
      this.$refs.renewModal.show(subscription);
    },

    cancelSubscription(subscription) {
      this.$refs.cancelModal.show(subscription);
    },

    resetFilters() {
      this.filters = {
        status: null,
        package_id: null,
        startDate: null,
        endDate: null
      };
      this.loadSubscriptions();
    },

    getStatusVariant(status) {
      const variants = {
        active: 'success',
        expired: 'secondary',
        cancelled: 'warning',
        past_due: 'danger'
      };
      return variants[status] || 'secondary';
    },

    displayRevenue(value) {
      return this.showRevenue ? value : '******'
    },

    fmtAmount(val) {
      return parseFloat(val || 0).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    // ── Customer search ───────────────────────────────────────────────────
    onCustomerSearch() {
      clearTimeout(this.customerSearchTimer)
      const q = this.customerSearch.trim()
      if (!q || q.length < 2) {
        this.customerSuggestions = []
        return
      }
      this.customerSearchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/allcustomers', { params: { search: q, limit: 8 } })
          this.customerSuggestions = data.data || data
        } catch { this.customerSuggestions = [] }
      }, 300)
    },

    selectCustomer(customer) {
      this.filters.customer_id = customer.id
      this.customerSearch = customer.full_name
      this.customerSuggestions = []
      this.loadSubscriptions()
    },

    clearCustomer() {
      this.filters.customer_id = null
      this.customerSearch = ''
      this.customerSuggestions = []
      this.loadSubscriptions()
    },

    // ── Quick check-in ────────────────────────────────────────────────────
    promptCheckIn(subscription) {
      this.checkInTarget = subscription
      this.$refs.checkInModal.show()
    },

    async confirmCheckIn() {
      if (!this.checkInTarget) return
      this.checkInLoading = true
      try {
        const res = await axios.post(`/api/subscriptions/${this.checkInTarget.id}/checkin`)
        this.$bvToast.toast(res.data.message, { title: 'Checked In', variant: 'success' })
        this.$refs.checkInModal.hide()
        this.loadSubscriptions()
      } catch (err) {
        const msg = err.response?.data?.message || 'Check-in failed.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger' })
      } finally {
        this.checkInLoading = false
      }
    },
  }
}
</script>

<style scoped>
/* Customer search dropdown */
.sub-customer-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 999;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,.1);
  max-height: 220px;
  overflow-y: auto;
}
.sub-customer-dropdown__item {
  display: block;
  padding: 8px 12px;
  font-size: 13px;
  color: #212529;
  text-decoration: none;
  border-bottom: 1px solid #f8f9fa;
}
.sub-customer-dropdown__item:hover { background: #f8f9fa; }
.sub-clear-customer {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 14px;
}
</style>
